export default function PrivacyPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Privacy Policy
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              Last updated: January 16, 2025
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 text-navy-600">
            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Introduction</h2>
              <p className="mb-4 leading-relaxed">
                Clarantis, Inc. ("Clarantis," "we," "us," or "our") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                when you use our governance, risk, and compliance (GRC) platform and related services
                (collectively, the "Services").
              </p>
              <p className="leading-relaxed">
                Please read this Privacy Policy carefully. By accessing or using our Services, you acknowledge
                that you have read, understood, and agree to be bound by this Privacy Policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Information We Collect</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Information You Provide to Us</h3>
              <p className="mb-3 leading-relaxed">We collect information you voluntarily provide when using our Services, including:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Account Information:</strong> Name, email address, company name, job title, and password when you create an account.</li>
                <li><strong>Profile Information:</strong> Additional details you add to your user profile.</li>
                <li><strong>Compliance Data:</strong> Documents, policies, evidence, risk assessments, vendor information, and other data you upload or create within the platform.</li>
                <li><strong>Communications:</strong> Information you provide when contacting us for support or feedback.</li>
                <li><strong>Payment Information:</strong> Billing details processed through our secure payment providers.</li>
              </ul>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Information Collected Automatically</h3>
              <p className="mb-3 leading-relaxed">When you access our Services, we automatically collect:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Usage Data:</strong> Pages visited, features used, actions taken, and time spent on the platform.</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address.</li>
                <li><strong>Log Data:</strong> Server logs including access times, error reports, and referring URLs.</li>
                <li><strong>Cookies and Similar Technologies:</strong> As described in our Cookie Policy.</li>
              </ul>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">Information from Third Parties</h3>
              <p className="mb-3 leading-relaxed">We may receive information from third-party integrations you connect, such as:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cloud service providers (AWS, Azure, GCP) for automated evidence collection</li>
                <li>Identity providers for single sign-on (SSO) authentication</li>
                <li>Collaboration tools (Atlassian Jira, Confluence) for workflow integration</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">How We Use Your Information</h2>
              <p className="mb-3 leading-relaxed">We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our Services</li>
                <li>Process transactions and send related information</li>
                <li>Send administrative messages, updates, and security alerts</li>
                <li>Respond to your comments, questions, and support requests</li>
                <li>Monitor and analyze usage trends to improve user experience</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations and enforce our terms</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Data Retention</h2>
              <p className="leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide
                Services. We also retain information as necessary to comply with legal obligations, resolve
                disputes, and enforce agreements. Compliance data may be retained longer based on regulatory
                requirements applicable to your industry.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Information Sharing and Disclosure</h2>
              <p className="mb-3 leading-relaxed">We do not sell your personal information. We may share information in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing.</li>
                <li><strong>Service Providers:</strong> With vendors who assist in providing our Services, bound by confidentiality obligations.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
                <li><strong>Protection of Rights:</strong> To protect the rights, property, and safety of Clarantis, our users, or others.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Data Security</h2>
              <p className="mb-3 leading-relaxed">
                We implement industry-standard security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Encryption of data in transit (TLS 1.3) and at rest (AES-256)</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Access controls and authentication requirements</li>
                <li>SOC 2 Type II certified infrastructure</li>
                <li>ISO 27001 compliant information security management</li>
              </ul>
              <p className="leading-relaxed">
                However, no method of transmission over the Internet or electronic storage is 100% secure.
                We cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Your Rights and Choices</h2>
              <p className="mb-3 leading-relaxed">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Access:</strong> Request a copy of your personal information.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information.</li>
                <li><strong>Portability:</strong> Request your data in a portable format.</li>
                <li><strong>Opt-Out:</strong> Opt out of certain data processing activities.</li>
              </ul>
              <p className="leading-relaxed">
                To exercise these rights, contact us at{' '}
                <a href="mailto:privacy@clarantis.com" className="text-primary-600 hover:text-primary-700 underline">
                  privacy@clarantis.com
                </a>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">International Data Transfers</h2>
              <p className="leading-relaxed">
                Your information may be transferred to and processed in countries other than your own.
                We ensure appropriate safeguards are in place, including Standard Contractual Clauses
                approved by relevant authorities.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Children's Privacy</h2>
              <p className="leading-relaxed">
                Our Services are not directed to individuals under 18. We do not knowingly collect
                personal information from children. If we learn we have collected such information,
                we will promptly delete it.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material
                changes by posting the new policy on this page and updating the "Last updated" date.
                Your continued use of the Services after changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">Contact Us</h2>
              <p className="mb-3 leading-relaxed">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email: <a href="mailto:privacy@clarantis.com" className="text-primary-600 hover:text-primary-700 underline">privacy@clarantis.com</a></li>
                <li>Address: Clarantis, Inc., Privacy Team</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
